import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const TIMELINE_SECTIONS = [
  { key: 'registration_timeline', label: 'Registration Process' },
  { key: 'membership_timeline', label: 'Membership Process' },
  { key: 'title_timeline', label: 'Title Process' },
  { key: 'owner_deceased_timeline', label: 'If Owner Passed Away, What Is The Process' },
];

const DEFAULT_ENTRY = { date: '', description: '', image_path: null };

function parseJson(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try { return JSON.parse(value); } catch (err) { return []; }
}

function TimelineSection({ title, rows, onChange, onUpload }) {
  return (
    <div className="card" style={{ padding: 18, marginBottom: 18 }}>
      <h3 style={{ marginBottom: 16 }}>{title}</h3>
      {rows.length === 0 ? (
        <p style={{ color: 'var(--text3)', fontStyle: 'italic' }}>No entries yet. Add one below.</p>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {rows.map((row, index) => (
            <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
              <div style={{ display: 'grid', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={row.date}
                    onChange={e => onChange(index, { ...row, date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    rows={3}
                    value={row.description}
                    onChange={e => onChange(index, { ...row, description: e.target.value })}
                    placeholder="Explain what happened"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-input"
                    onChange={e => e.target.files[0] && onUpload(index, e.target.files[0])}
                  />
                </div>
                {row.image_path ? (
                  <img
                    src={`https://ems.ourschool.lk/uploads/${row.image_path}`}
                    alt="Timeline"
                    style={{ width: '100%', borderRadius: 10, objectFit: 'cover', minHeight: 130 }}
                  />
                ) : (
                  <div style={{ minHeight: 130, borderRadius: 10, background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13 }}>
                    No image selected
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>)}
      <button
        className="btn btn-secondary"
        style={{ marginTop: 12 }}
        onClick={() => onChange(rows.length, { ...DEFAULT_ENTRY })}
      >
        + Add entry
      </button>
    </div>
  );
}

export default function RegistrationTimeline({ member }) {
  const [entries, setEntries] = useState({
    registration_timeline: [],
    membership_timeline: [],
    title_timeline: [],
    owner_deceased_timeline: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!member) return;
    setEntries({
      registration_timeline: parseJson(member.registration_timeline),
      membership_timeline: parseJson(member.membership_timeline),
      title_timeline: parseJson(member.title_timeline),
      owner_deceased_timeline: parseJson(member.owner_deceased_timeline),
    });
  }, [member]);

  const updateSection = (section, index, value) => {
    setEntries(prev => {
      const list = [...prev[section]];
      if (index === list.length) list.push(value);
      else list[index] = value;
      return { ...prev, [section]: list };
    });
  };

  const handleUpload = async (section, index, file) => {
    if (!member?.id) { toast.error('Save personal info first'); return; }
    const fd = new FormData();
    const fieldName = `${section}_${index}_image`;
    fd.append(fieldName, file);

    try {
      const { data } = await api.post(`/members/${member.id}/upload`, fd);
      const uploaded = Array.isArray(data.files) ? data.files[0] : null;
      if (!uploaded?.file_path) throw new Error('Upload response missing file path');
      updateSection(section, index, { ...entries[section][index], image_path: uploaded.file_path });
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Image upload failed');
    }
  };

  const save = async () => {
    if (!member?.id) { toast.error('Save personal info first'); return; }
    setSaving(true);
    try {
      const payload = {
        registration_timeline: JSON.stringify(entries.registration_timeline),
        membership_timeline: JSON.stringify(entries.membership_timeline),
        title_timeline: JSON.stringify(entries.title_timeline),
        owner_deceased_timeline: JSON.stringify(entries.owner_deceased_timeline),
      };
      await api.put(`/members/${member.id}`, payload);
      toast.success('Timeline saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!member?.id) {
    return <div className="empty-state"><p>Save personal info first to manage registration and membership timelines.</p></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {TIMELINE_SECTIONS.map(section => (
        <TimelineSection
          key={section.key}
          title={section.label}
          rows={entries[section.key]}
          onChange={(index, value) => updateSection(section.key, index, value)}
          onUpload={(index, file) => handleUpload(section.key, index, file)}
        />
      ))}
      <div className="section-save-bar">
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Timeline'}
        </button>
      </div>
    </div>
  );
}
